import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// Components

// Configs

// Interfaces
import { IChannel } from 'v2/modules/shared';

import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// Services
import { ChannelApiService } from '../../../shared/services/api/channel/channel-api.service';
import { ChannelProductMappingApiService } from '../../../shared/services/api/channel/channel-product-mapping-api.service';
import {
  IAppIntegrationAccountProductOverride,
  IChannelProductMappingFeatureFlag,
} from '../../interfaces';
import { ChannelProductSyncProductsFormComponent } from '../channel-product-sync-products-form/channel-product-sync-products-form.component';

import { ImportTypeConstant } from './../../../../../app/core/_constant/imports.constants';
import { ImporterService } from './../../../../../app/core/tools/services/importer.service';

@Component({
  selector: 'lum-channel-product-mapping-v2',
  templateUrl: './channel-product-mapping-v2.component.html',
  styleUrls: ['./channel-product-mapping-v2.component.scss'],
})
export class ChannelProductMappingV2Component implements OnInit, OnDestroy {
  @Input()
  channel :IChannel | null = null;

  @Input()
  featureFlags :IChannelProductMappingFeatureFlag;

  syncLoading :boolean = false;
  selectedRows :IAppIntegrationAccountProductOverride[] = [];
  pushUpdateLoading :boolean = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private cd :ChangeDetectorRef,
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private channelApiService :ChannelApiService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private importerService :ImporterService,
  ) {}

  ngOnInit() :void {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onOpenSyncProductsModal() :void {
    this.dialog
      .open(ChannelProductSyncProductsFormComponent, {
        width: DialogConfig.sizes.small,
        data: {
          withPushStocksAppIntegrationAccount: true,
          channel: this.channel,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params :any) => {
        if (params) {
          if (params.channelIds.length > 0) {
            const ids = [...new Set(params.channelIds)];
            params.appIntegrationAccountIds = ids.join(',');
          }
          this.syncProductsByChannelPushStockOmsIds(params);
        }
      });
  }

  onSyncProductsByChannelPushStockOmsIds() :void {
    this.syncProductsByChannelPushStockOmsIds({
      appIntegrationAccountIds:
        this.channel?.pushStocksAppIntegrationAccountId.toString(),
    });
  }

  onPushUpdates() :void {
    this.pushUpdateLoading = true;

    this.pushProductUpdates(this.formatPushUpdatesRequestData())
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.pushUpdateLoading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { success } = response;
          this.snackBar.open(
            success || 'Successfully pushed product updates.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (err) => {
          const { error } = err;
          this.snackBar.open(error || 'Failed to push updates.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onPushAllProducts() :void {
    this.pushUpdateLoading = true;

    this.channelProductMappingApiService
      .pushAllProducts(this.channel?.id || null)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.pushUpdateLoading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { success } = response;
          this.snackBar.open(
            success || 'Successfully pushed all product updates.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (err) => {
          const { error } = err;
          this.snackBar.open(error || 'Failed to push all updates.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSelectionChanged(selectedRows :IAppIntegrationAccountProductOverride[]) :void {
    this.selectedRows = selectedRows;
  }

  onExportProduct() :void {
    this.importerService
      .getExportTemplateData({
        type: ImportTypeConstant.APP_INTEGRATION_ACCOUNT_PRODUCTS,
        channelId: this.channel?.id?.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response?.length) {
            this.importerService.exportToExcel(response, 'Channel Products');
          } else {
            this.snackBar.open('No products found to export', 'Info', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to export products', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private formatPushUpdatesRequestData() :{
    channelId :number;
    productIds :number[];
  }[] {
    const result = this.selectedRows.reduce((acc, item) => {
      const channelId = item.channelId;
      const productId = item.id;

      if (!acc[channelId]) {
        acc[channelId] = [];
      }

      acc[channelId].push(productId);

      return acc;
    }, {});

    return Object.keys(result).map((channelId) => ({
      channelId: parseInt(channelId),
      productIds: result[channelId],
    }));
  }

  private syncProductsByChannelPushStockOmsIds(params :{
    [param :string] :string | string[];
  }) :void {
    this.syncLoading = true;
    this.channelApiService
      .syncProductsByAppIntegrationAccountIds(params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.syncLoading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(
            'Sync in progress - this may take a while. Please refresh the page after a few minutes.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (error :any) => {
          this.snackBar.open('Failed to sync products.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private pushProductUpdates(
    data :{
      channelId :number;
      productIds :number[];
    }[],
  ) :Observable<any> {
    return this.channelProductMappingApiService.pushUpdates({
      items: data,
    });
  }

  get hasChannel() :boolean {
    return !!this.channel;
  }
}
